import { useEffect, useRef, useState, type JSX } from 'react';

import { Icon, Num } from '@bookora/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import type { OnboardingStepResponse } from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';
import { LinkButton } from '@/components/link-button';
import { settingsKeys } from '@/features/settings/hooks';

import { RUNTIME_COPY } from './runtime-copy';
import { SETUP_CHECKLIST_PATH, openStepForLocation, useSetupProgress } from './setup-progress';
import { setupStepHref } from './setup-step-href';

/**
 * A slim, non-modal bar above the page while the owner works through setup on
 * a step's OWN page — «کارکنان و نقش‌ها», «خدمات و تعرفه‌ها», the calendar, the
 * patient import, the stock receipt (critic «setup-step-return-bar»). Those
 * pages had no way back to the checklist and no «next step»: after inviting a
 * colleague the owner had to find the dashboard card again.
 *
 *   while the step is open   «راه‌اندازی: ۲ از ۶ · این مرحله: دعوت کارکنان»  [همهٔ مراحل]
 *   once it is done          «این مرحله انجام شد.»  [مرحلهٔ بعد: …] [همهٔ مراحل]
 *
 * «Done» is noticed promptly: while the bar is up, any successful save
 * refreshes the checklist. `/settings…` has its own banner and is left alone.
 * Shown only to whom the dashboard card is (`useSetupProgress().visible`).
 */
export function SetupStepBar(): JSX.Element | null {
  const setup = useSetupProgress();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();

  const onSettings =
    location.pathname === '/settings' || location.pathname.startsWith('/settings/');
  const here =
    setup.visible && !onSettings
      ? openStepForLocation(setup.steps, location.pathname, location.search)
      : null;

  // The step this page visit is about, kept once it is found so the bar can
  // say «انجام شد» when the checklist later marks it done.
  const [visitStep, setVisitStep] = useState<{ path: string; key: string } | null>(null);
  const visitPath = useRef(location.pathname);
  useEffect(() => {
    if (visitPath.current !== location.pathname) {
      visitPath.current = location.pathname;
      setVisitStep(null);
    }
  }, [location.pathname]);
  useEffect(() => {
    if (here && here.key !== visitStep?.key)
      setVisitStep({ path: location.pathname, key: here.key });
  }, [here, visitStep, location.pathname]);

  const tracked: OnboardingStepResponse | null =
    visitStep && visitStep.path === location.pathname
      ? (setup.steps.find((step) => step.key === visitStep.key) ?? null)
      : null;
  const show = !onSettings && setup.visible && (here !== null || tracked?.done === true);

  // A save on this page may have finished the step: re-read the checklist.
  useEffect(() => {
    if (!show) return;
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'success') {
        void queryClient.invalidateQueries({ queryKey: settingsKeys.onboarding(activeClinicId) });
      }
    });
  }, [show, queryClient, activeClinicId]);

  if (!show) return null;
  const step = here ?? tracked;
  if (!step) return null;
  const done = step.done;
  const next = setup.next && setup.next.key !== step.key ? setup.next : null;

  return (
    <section className="bk-setup-bar" aria-label={RUNTIME_COPY.setup.region}>
      <span className="bk-setup-bar__icon" aria-hidden="true">
        <Icon name={done ? 'check' : 'sparkle'} size={16} />
      </span>
      <p className="bk-setup-bar__text" role="status">
        {done ? (
          RUNTIME_COPY.setup.stepDone
        ) : (
          <>
            {RUNTIME_COPY.setup.lead} <Num value={setup.completed} /> {RUNTIME_COPY.setup.of}{' '}
            <Num value={setup.total} />
            <span aria-hidden="true"> · </span>
            {RUNTIME_COPY.setup.thisStep} <strong>{step.title}</strong>
          </>
        )}
      </p>
      <span className="bk-setup-bar__actions">
        {done && next ? (
          <LinkButton type="primary" size="small" to={setupStepHref(next)}>
            {RUNTIME_COPY.setup.nextStep(next.title)}
          </LinkButton>
        ) : null}
        <LinkButton type={done && next ? 'text' : 'link'} size="small" to={SETUP_CHECKLIST_PATH}>
          {RUNTIME_COPY.setup.allSteps}
        </LinkButton>
      </span>
    </section>
  );
}
